import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Loader2, Target, Clock, CheckCircle2 } from 'lucide-react';
import { getTracks } from '@/lib/data-access/tracks';
import { setUserTrackSelection } from '@/lib/data-access/user-track';
import { getTrackSkills } from '@/lib/data-access/tracks';
import { getTrackResources } from '@/lib/data-access/resources';
import { upsertUserSkill } from '@/lib/data-access/user-skills';
import { upsertUserResource } from '@/lib/data-access/user-resources';
import { useToast } from '@/hooks/use-toast';
import type { Track } from '@/types/database';

const TIME_HORIZONS = [
  { weeks: 12, label: '12 Weeks', description: 'Accelerated - 20+ hrs/week' },
  { weeks: 24, label: '24 Weeks', description: 'Standard - 10-15 hrs/week' },
  { weeks: 52, label: '52 Weeks', description: 'Extended - 5-10 hrs/week' },
];

export default function OnboardingTrackSelection() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tracks, setTracks] = useState<Track[]>([]);
  const [selectedTrackId, setSelectedTrackId] = useState<string>('');
  const [selectedTimeHorizon, setSelectedTimeHorizon] = useState<number>(24);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    async function loadTracks() {
      try {
        const data = await getTracks();
        setTracks(data);
        if (data.length > 0) {
          setSelectedTrackId(data[0].id);
        }
      } catch (error) {
        console.error('Error loading tracks:', error);
        toast({
          title: 'Error',
          description: 'Failed to load career tracks. Please refresh the page.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    }

    loadTracks();
  }, [toast]);

  const handleSubmit = async () => {
    if (!selectedTrackId) {
      toast({
        title: 'Selection Required',
        description: 'Please select a career track.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // 1. Save track selection
      await setUserTrackSelection(selectedTrackId, selectedTimeHorizon);

      // 2. Seed user_skills with Gap status
      const trackSkills = await getTrackSkills(selectedTrackId);
      await Promise.all(
        trackSkills.map((ts) =>
          upsertUserSkill({
            user_id: '', // Set by DAL
            skill_id: ts.skill_id,
            status: 'Gap',
            current_level: 0,
            source: 'TrackDefault',
            notes: null,
          })
        )
      );

      // 3. Seed user_resources with NotStarted status
      const trackResources = await getTrackResources(selectedTrackId);
      await Promise.all(
        trackResources.map((tr) =>
          upsertUserResource({
            user_id: '', // Set by DAL
            resource_id: tr.resource_id,
            status: 'NotStarted',
            started_at: null,
            completed_at: null,
            notes: null,
          })
        )
      );

      toast({
        title: 'Plan Created',
        description: 'Your personalized transition plan is ready.',
      });

      // TODO: Create user agent here (future prompt)

      navigate('/');
    } catch (error) {
      console.error('Error creating plan:', error);
      toast({
        title: 'Error',
        description: 'Failed to create your plan. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const selectedTrack = tracks.find((t) => t.id === selectedTrackId);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-primary">Choose Your Target Role</CardTitle>
          <CardDescription>
            Select a career track and your desired timeline. We'll create a personalized learning plan.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Track Selection */}
          <div>
            <Label className="text-lg font-semibold mb-4 block">Career Track</Label>
            <RadioGroup value={selectedTrackId} onValueChange={setSelectedTrackId}>
              <div className="space-y-3">
                {tracks.map((track) => (
                  <div key={track.id} className="flex items-start space-x-3">
                    <RadioGroupItem value={track.id} id={track.id} className="mt-1" />
                    <Label
                      htmlFor={track.id}
                      className="flex-1 cursor-pointer p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <Target className="h-5 w-5 text-primary mt-0.5" />
                        <div className="flex-1">
                          <div className="font-semibold text-foreground">{track.name}</div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {track.description}
                          </div>
                          <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                            <span>Difficulty: Level {track.difficulty_level}</span>
                            <span>Est. {track.estimated_months_to_ready} months</span>
                          </div>
                        </div>
                      </div>
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </div>

          {/* Time Horizon Selection */}
          <div>
            <Label className="text-lg font-semibold mb-4 block">Time Horizon</Label>
            <RadioGroup
              value={selectedTimeHorizon.toString()}
              onValueChange={(v) => setSelectedTimeHorizon(parseInt(v))}
            >
              <div className="space-y-3">
                {TIME_HORIZONS.map((horizon) => (
                  <div key={horizon.weeks} className="flex items-start space-x-3">
                    <RadioGroupItem
                      value={horizon.weeks.toString()}
                      id={`horizon-${horizon.weeks}`}
                      className="mt-1"
                    />
                    <Label
                      htmlFor={`horizon-${horizon.weeks}`}
                      className="flex-1 cursor-pointer p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <Clock className="h-5 w-5 text-primary mt-0.5" />
                        <div className="flex-1">
                          <div className="font-semibold text-foreground">{horizon.label}</div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {horizon.description}
                          </div>
                        </div>
                      </div>
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </div>

          {/* Summary */}
          {selectedTrack && (
            <div className="p-4 rounded-lg bg-muted/50 border border-border">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <div className="font-semibold text-foreground">Your Plan</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Track: <span className="text-foreground">{selectedTrack.name}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Timeline:{' '}
                    <span className="text-foreground">
                      {TIME_HORIZONS.find((h) => h.weeks === selectedTimeHorizon)?.label}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Button
            onClick={handleSubmit}
            className="w-full"
            size="lg"
            disabled={isSubmitting || !selectedTrackId}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Your Plan...
              </>
            ) : (
              'Create My Plan'
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
